import React, { memo, useMemo } from "react";
import moment from "moment";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import { HiOutlineTruck } from "react-icons/hi2";
import {
  durationMinus2Hours,
  formatAmount,
  parseBooleanVal,
  quantityFromBreakdown,
  rosterInvoiceLineTotal,
  rosterInvoiceTravelDenominator,
  timeToQuantity,
  truncateDecimal,
} from "Utils/utils";
import { getLineItemLabel } from "Constants/constant";
import { formatBreakdownClaimTypeSuffix } from "pages/Rostering/WorkerShift/components/advanceModeSplitDefaults";
import "../CreateRosterInvoicing/roster-invoice-breakdown.css";

const formatMoney = (value) =>
  value != null && !Number.isNaN(Number(value)) ? `$${formatAmount(value)}` : null;

/** Time cell: single date with an optional start–end hours range (mirrors create). */
const formatLineSchedule = (el) => {
  const start = el?.start_time ? moment(el.start_time) : null;
  const end = el?.end_time ? moment(el.end_time) : null;

  if (!start && !end) return { date: "Time not recorded", hours: null };

  if (start && end) {
    const sameDay = start.format("YYYY-MM-DD") === end.format("YYYY-MM-DD");
    return {
      date: sameDay
        ? start.format("ddd, D MMM YY")
        : `${start.format("ddd, D MMM")} – ${end.format("ddd, D MMM YY")}`,
      hours: `${start.format("hh:mm A")} – ${end.format("hh:mm A")}`,
    };
  }

  const single = start || end;
  return {
    date: single.format("ddd, D MMM YY"),
    hours: single.format("hh:mm A"),
  };
};

const isBasicShiftLine = (el) =>
  !el?.ndis_support_item_number && !el?.ndis_support_item_name;

const isTravelLine = (el) =>
  parseBooleanVal(el?.is_travel) ||
  el?.is_travel === 1 ||
  String(el?.is_travel) === "1";

/** Quantity — preserves the existing preview calculation exactly. */
const getLineQuantityValue = (el) => {
  if (el?.quantity != null && el?.quantity !== "") {
    const parsed = parseFloat(el.quantity);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (el?.start_time && el?.end_time) {
    return quantityFromBreakdown(el.start_time, el.end_time, el?.duration, {
      sleepover: el?.is_sleepover,
      isSta: el?.is_sta,
      parentId: el?.parent_id,
    });
  }
  if (el?.duration) {
    return timeToQuantity(
      el?.parent_id != null ? durationMinus2Hours(el.duration) : el.duration,
      el?.parent_id != null ? false : Boolean(el?.is_sleepover || el?.is_sta)
    );
  }
  return null;
};

const getLineQuantity = (el) => {
  const quantity = getLineQuantityValue(el);
  return quantity == null ? null : formatAmount(quantity);
};

/**
 * Per-participant hourly rate.
 *
 * The invoice API already applies the line ratio (catalog ÷ participants):
 * $82.57 at 1:1, $100 at 1:2 → $50. Do not re-derive from
 * display_amount ÷ rounded qty — 2h 8m truncates to 2.13 and invents $82.69.
 */
const getLineRate = (el) => {
  if (el?.rate == null || el?.rate === "") return null;
  const parsed = parseFloat(el.rate);
  return Number.isNaN(parsed) ? null : truncateDecimal(parsed);
};

/**
 * Ratio for a breakdown row. Each V5 advance line carries its own split ratio
 * (sleepover 1:4 alongside active hours 1:3), so the detail-level ratio — which
 * reads "mixed" in that case — is only a fallback.
 */
const getLineRatio = (el, fallbackRatio) =>
  el?.ratio != null && el?.ratio !== "" ? el.ratio : fallbackRatio;

/** Line total: displayed 2dp qty × applied rate (same numbers the user sees). */
const getLineTotal = (el, totalParticipants) => {
  const quantity = getLineQuantityValue(el);
  const rate = getLineRate(el);
  if (quantity != null && rate != null) {
    return truncateDecimal(quantity * rate);
  }
  if (el?.display_amount != null && el?.display_amount !== "") {
    return parseFloat(el.display_amount);
  }
  if (el?.duration && el?.rate) {
    return rosterInvoiceLineTotal(el.duration, el.rate, totalParticipants, {
      sleepover: el?.is_sleepover,
      isSta: el?.is_sta,
      parentId: el?.parent_id,
      startTime: el?.start_time,
      endTime: el?.end_time,
    });
  }
  if (el?.total_amount) {
    return truncateDecimal(parseFloat(el.total_amount));
  }
  return null;
};

const InvoicePreviewTable = (props) => {
  const {
    item,
    careSector,
    showClaimTypeScheduleTimesheets = true,
    showTravelToggleScheduleTimesheets = true,
  } = props;
  const [expandedRows, setExpandedRows] = React.useState([]);
  const isExpanded = !expandedRows.includes(item.uuid);

  const workerAvatarData = useMemo(() => {
    if (!item?.workers) return [];
    return Object.values(item.workers).map((worker) => {
      const [first_name, last_name] = worker?.split(" ");
      return { first_name, last_name };
    });
  }, [item?.workers]);

  const workerCount = item?.workers ? Object.keys(item.workers).length : 0;

  const rowRatio = item?.ratio ? item.ratio : "1:1";
  const timesheets = item?.invoiceTimesheets ?? [];
  const lineItemColumnLabel = getLineItemLabel(careSector) || "NDIS item";
  const cancelled = parseBooleanVal(item?.cancelled);

  const travelTotal = useMemo(() => {
    if (!item?.travel_item_number) return null;
    if (item?.travel_amount != null && item.travel_amount !== "") {
      const parsed = parseFloat(item.travel_amount);
      return Number.isNaN(parsed) ? null : truncateDecimal(parsed);
    }
    if (!item?.km || !item?.travel_item_price) return null;
    return truncateDecimal(
      (parseFloat(item.km) * parseFloat(item.travel_item_price)) /
        rosterInvoiceTravelDenominator(item)
    );
  }, [
    item?.travel_item_number,
    item?.travel_amount,
    item?.km,
    item?.travel_item_price,
    item?.travel_participant_count,
    item?.total_participants,
  ]);

  const shiftTotal = useMemo(() => {
    const persisted = parseFloat(item?.total_amount);
    if (!Number.isNaN(persisted) && item?.total_amount != null && item?.total_amount !== "") {
      return truncateDecimal(persisted);
    }

    const lineSum = timesheets.length
      ? truncateDecimal(
          timesheets.reduce(
            (sum, el) => sum + (getLineTotal(el, item?.total_participants) ?? 0),
            0
          )
        )
      : null;

    if (lineSum == null && travelTotal == null) return null;

    return truncateDecimal((lineSum ?? 0) + (travelTotal ?? 0));
  }, [timesheets, item?.total_participants, item?.total_amount, travelTotal]);

  const handleRowClick = () => {
    setExpandedRows((prev) => {
      if (prev.includes(item.uuid)) {
        return prev.filter((rowId) => rowId !== item.uuid);
      }
      return [...prev, item.uuid];
    });
  };

  const travelRateDisplay = useMemo(() => {
    if (!item?.travel_item_price) return null;
    if (item?.km > 0 && travelTotal != null) {
      return truncateDecimal(travelTotal / parseFloat(item.km));
    }
    return truncateDecimal(
      parseFloat(item.travel_item_price) / rosterInvoiceTravelDenominator(item)
    );
  }, [
    item?.travel_item_price,
    item?.travel_participant_count,
    item?.total_participants,
    item?.km,
    travelTotal,
  ]);

  return (
    <>
      <tr className='text-center even:bg-white odd:bg-gray-50'>
        <td
          className='whitespace-nowrap pl-2 pr-1 py-4 text-sm'
          onClick={handleRowClick}
        >
          <button
            type='button'
            className='flex cursor-pointer items-center rounded-md p-0.5 text-gray-500 transition-colors hover:bg-gray-100'
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse breakdown" : "Expand breakdown"}
          >
            {isExpanded ? (
              <BiChevronUp size={22} />
            ) : (
              <BiChevronDown size={22} />
            )}
          </button>
        </td>
        <td className='px-3 py-4 text-sm text-gray-500 text-center capitalize'>
          <div className='text-gray-500 capitalize'>
            {item?.invoice_number || "-"}
          </div>
        </td>
        <td className='whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center'>
          <div className='flex justify-center text-gray-500'>
            {item?.workers &&
              (workerCount === 1 ? (
                Object.values(item?.workers)
              ) : (
                <AvatarGroup
                  arrayData={workerAvatarData}
                  cssClass='mt-0'
                  maxLength={10}
                />
              ))}
          </div>
        </td>
        <td className='whitespace-nowrap px-3 py-4 text-sm text-center'>
          <div className='text-gray-500'>
            {item?.worker_start_time
              ? moment(item?.worker_start_time).format("ddd, D MMM YY | hh:mm A")
              : "N/A"}
          </div>
        </td>
        <td className='whitespace-nowrap text-sm text-center'>
          <div className='text-gray-500'>
            {item?.worker_end_time
              ? moment(item?.worker_end_time).format("ddd, D MMM YY | hh:mm A")
              : "N/A"}
          </div>
        </td>
        <td className='whitespace-nowrap text-sm text-center'>
          <div className='text-gray-500'>{rowRatio}</div>
        </td>
        <td className='whitespace-nowrap px-3 py-4 text-sm text-center'>
          <div className='text-gray-500'>
            {moment(item?.delivered_date).format("ddd, D MMM YY")}
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={10} className='bg-[#eef2f7] px-3 py-4 sm:px-5'>
            <div className='rib-panel'>
              <div className='rib-panel-toolbar'>
                <div>
                  <h4 className='rib-panel-title'>Billable breakdown</h4>
                  <p className='rib-panel-subtitle'>
                    Review allocated service items for this shift
                  </p>
                </div>
                <div className='rib-panel-meta'>
                  {timesheets.length > 0 ? (
                    <span className='rib-chip'>
                      {timesheets.length} line
                      {timesheets.length !== 1 ? "s" : ""}
                    </span>
                  ) : null}
                  {shiftTotal != null ? (
                    <span className='rib-chip rib-chip--total'>
                      Shift total {formatMoney(shiftTotal)}
                    </span>
                  ) : null}
                </div>
              </div>

              {timesheets.length > 0 ? (
                <div className='rib-breakdown' role='table'>
                  <div className='rib-grid-head' role='row'>
                    <span role='columnheader'>Time</span>
                    <span role='columnheader'>Support / service</span>
                    <span role='columnheader'>{lineItemColumnLabel}</span>
                    <span role='columnheader'>Ratio</span>
                    <span role='columnheader'>Qty</span>
                    <span role='columnheader'>Hourly Rate</span>
                    <span role='columnheader'>Total Value</span>
                  </div>

                  <div className='rib-participant-block'>
                    {timesheets.map((el, index) => {
                      const schedule = formatLineSchedule(el);
                      const basic = isBasicShiftLine(el);
                      const quantity = getLineQuantity(el);
                      const rate = getLineRate(el);
                      const total = getLineTotal(el, item?.total_participants);
                      const supportName = el?.ndis_support_item_name;
                      const itemCode = el?.ndis_support_item_number;

                      return (
                        <div
                          className='rib-line-row'
                          role='row'
                          key={el?.uuid ?? `line-${index}`}
                        >
                          <div className='rib-line-time' role='cell'>
                            <span className='rib-line-date'>
                              {schedule.date}
                            </span>
                            {schedule.hours ? (
                              <span className='rib-line-hours'>
                                {schedule.hours}
                              </span>
                            ) : null}
                          </div>

                          <div className='rib-line-service' role='cell'>
                            {basic ? (
                              <>
                                <p className='rib-line-service-empty'>
                                  No service item allocated
                                </p>
                                <span className='rib-line-badge'>
                                  Basic shift
                                </span>
                              </>
                            ) : supportName ? (
                              <p className='rib-line-service-name'>
                                {supportName}
                                {showTravelToggleScheduleTimesheets &&
                                  isTravelLine(el) &&
                                  " - Travel"}
                                {showClaimTypeScheduleTimesheets
                                  ? formatBreakdownClaimTypeSuffix(el)
                                  : null}
                              </p>
                            ) : (
                              <p className='rib-line-service-empty'>—</p>
                            )}
                          </div>

                          <div className='rib-line-code' role='cell'>
                            {showTravelToggleScheduleTimesheets ? (
                              <>
                                {itemCode || "—"}
                                {cancelled ? " (Cancelled)" : ""}
                              </>
                            ) : (
                              <>
                                {itemCode || "—"}
                                {supportName ? ` - ${supportName}` : ""}
                                {cancelled &&
                                !String(supportName || "")
                                  .toLowerCase()
                                  .includes("(cancelled)")
                                  ? " (Cancelled)"
                                  : ""}
                                {el?.line_item_source && (
                                  <span className='ml-1.5 text-xs text-gray-400'>
                                    (
                                    {el.line_item_source === "custom"
                                      ? "Service type"
                                      : "Support item"}
                                    )
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          <div className='rib-line-num rib-line-ratio' role='cell'>
                            {getLineRatio(el, rowRatio)}
                          </div>

                          <div
                            className={`rib-line-num${
                              quantity == null ? " rib-line-num--muted" : ""
                            }`}
                            role='cell'
                          >
                            {quantity ?? "—"}
                          </div>

                          <div
                            className={`rib-line-num${
                              rate == null ? " rib-line-num--muted" : ""
                            }`}
                            role='cell'
                          >
                            {formatMoney(rate) ?? "—"}
                          </div>

                          <div
                            className={`rib-line-num rib-line-num--total${
                              total == null ? " rib-line-num--muted" : ""
                            }`}
                            role='cell'
                          >
                            {formatMoney(total) ?? "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className='rib-empty'>
                  <p className='rib-empty-title'>No shift breakdown</p>
                  <p className='rib-empty-text'>
                    There is no breakdown for this timesheet.
                  </p>
                </div>
              )}

              {item?.travel_item_number ? (
                <section className='rib-travel'>
                  <div className='rib-travel-head'>
                    <span className='rib-travel-icon' aria-hidden>
                      <HiOutlineTruck className='h-4 w-4' />
                    </span>
                    <h5 className='rib-travel-title'>Travel charges</h5>
                  </div>
                  <div className='rib-travel-body'>
                    <div>
                      <p className='rib-travel-field-label'>Date</p>
                      <p className='rib-travel-field-value'>
                        {item?.shift_start_time
                          ? moment(item.shift_start_time).format("ddd, D MMM YY")
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className='rib-travel-field-label'>Service item</p>
                      <p className='rib-travel-field-value'>
                        {item?.travel_item_number}
                        {item?.travel_item_name
                          ? ` · ${item.travel_item_name}`
                          : ""}
                      </p>
                    </div>
                    <div>
                      <p className='rib-travel-field-label'>Distance</p>
                      <p className='rib-travel-field-value'>
                        {item?.km ? `${item.km} km` : "—"}
                        {travelRateDisplay != null
                          ? `${formatMoney(travelRateDisplay)}`
                          : ""}
                      </p>
                    </div>
                    <div>
                      <p className='rib-travel-field-label'>Total</p>
                      <p className='rib-travel-field-value rib-travel-field-value--total'>
                        {formatMoney(travelTotal) ?? "—"}
                      </p>
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default memo(InvoicePreviewTable);
